import { collection, setDoc, arrayUnion } from 'firebase/firestore';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';

import { logAnalyticsEvent } from '../analytics/analytics';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import EventRosterInterface from '../interfaces/EventRosterInterface';
import { ChatMessage } from '../types/ChatMessage';
import Event, { EVENT_STATUSES } from '../types/Event';
import EventDay from '../types/EventDay';
import { CurrentDateTime, generateFirestoreId, replaceUndefinedWithDeleteField, stripUndefined } from '../utils/utils';


function sortDays(days: EventDay[]): EventDay[] {
  return [...days].sort((a, b) => {
    const aTime = a.datetime instanceof Timestamp ? a.datetime.seconds : 0;
    const bTime = b.datetime instanceof Timestamp ? b.datetime.seconds : 0;
    return aTime - bTime;
  });
}

// Lazy backfill, not a migration: every day already gets an itemId at creation time
// (useEventEditorState.ts), but days that predate that existed with none. Rather than a one-time
// migration script, any day still missing one gets it the next time an admin saves ANY change to
// that event's day list — after which that event's days are permanently ID-stable. Until then,
// day-index remapping (handleEventDaysUpdated.ts) simply can't anchor that day and falls back to
// today's behavior for it, same as before this existed.
function backfillDayIds(days: EventDay[]): EventDay[] {
  return days.map((day) => day.itemId ? day : { ...day, itemId: generateFirestoreId() });
}

function calculateEndDatetime(eventDays: EventDay[]): Timestamp {
  if (!eventDays || eventDays.length === 0) {
    return Timestamp.now();
  }

  let latestEndSeconds = 0;

  for (const day of eventDays) {
    if (!day.datetime) continue;

    // Get seconds from Timestamp
    if (day.datetime instanceof Timestamp) {
      console.log('day.datetime is a Timestamp');
    } else {
      console.log('day.datetime is not a Timestamp');
    }
    const startSeconds = day.datetime instanceof Timestamp ? day.datetime.seconds : (day.datetime as Timestamp).seconds;

    // Add duration in seconds (duration is in minutes)
    const duration = day.duration || 60;
    const endSeconds = startSeconds + duration * 60;

    if (endSeconds > latestEndSeconds) {
      latestEndSeconds = endSeconds;
    }
  }

  return latestEndSeconds > 0 ? Timestamp.fromMillis(latestEndSeconds * 1000) : Timestamp.now();
}

export const useEventMutators = () => {
  const { isLeagueAdmin, userState } = useData();

  const addEvent = async (event: Event): Promise<Event | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      return null;
    }

    if (!event.leagueId || !event.orgId) {
      return null;
    }

    if (!isLeagueAdmin(event.leagueId, event.orgId)) {
      return null;
    }

    try {
      // Use provided itemId if available, otherwise generate a new one
      const eventRef = event.itemId
        ? doc(firestore, `org/${event.orgId}/event/${event.itemId}`)
        : doc(collection(firestore, `org/${event.orgId}/event`));

      const sortedDays = sortDays(backfillDayIds(event.days));

      const startDatetime = sortedDays.reduce(
        (earliest, day) => (day.datetime < earliest ? day.datetime : earliest),
        sortedDays[0].datetime
      );

      const endDatetime = calculateEndDatetime(sortedDays);
      const newEvent = {
        ...event,
        days: sortedDays,
        createTime: Timestamp.now(),
        creatorId: userState.userProfile?.itemId,
        startDatetime: startDatetime,
        endDatetime: endDatetime,
        itemId: eventRef.id,
      };

      await setDoc(eventRef, stripUndefined(newEvent)).then(() => {
        console.log('Event added:', newEvent);
        logAnalyticsEvent('event_created', { org_id: event.orgId, league_id: event.leagueId });
      }).catch((error) => {
        console.error('Error adding event:', newEvent, "error: ", error);
      });
      return newEvent;
    } catch (error) {
      console.error('Error adding event:', error);
      return null;
    }
  };

  const deleteEvent = async (event: Event): Promise<void> => {
    if (!event.itemId || !event.orgId) {
      console.error('Event itemId or orgId not found:', event.itemId, event.orgId);
      return;
    }

    if (!isLeagueAdmin(event.leagueId, event.orgId)) {
      console.error('Permission denied. User:', userState.userProfile?.itemId, 'Event:', event.itemId);
      return;
    }

    try {
      const eventRef = doc(firestore, `org/${event.orgId}/event/${event.itemId}`);
      // Soft delete only — the doc and its subcollections stay for history.
      await updateDoc(eventRef, { status: EVENT_STATUSES.DELETED });
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const updateEvent = async (event: Partial<Event>): Promise<void> => {
    if (!event.orgId || !event.itemId || !event.leagueId) {
      console.error('Missing required fields:', {
        orgId: event.orgId,
        itemId: event.itemId,
        leagueId: event.leagueId,
      });
      return;
    }

    if (!isLeagueAdmin(event.leagueId, event.orgId)) {
      console.error('Permission denied. User:', userState.userProfile?.itemId, 'Event:', event.itemId);
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { orgId, itemId, leagueId, courseIds: _courseIds, coursePairs: _coursePairs, ...eventUpdates } = event;
      const eventRef = doc(firestore, `org/${orgId}/event/${itemId}`);
      const updates = {
        ...eventUpdates,
      };

      // TODO: This should be moved to the backend.
      if (event.days) {
        const sortedDays = sortDays(backfillDayIds(event.days));

        updates.days = sortedDays;
        updates.startDatetime = sortedDays.reduce(
          (earliest, day) => (day.datetime < earliest ? day.datetime : earliest),
          sortedDays[0].datetime
        );
        updates.endDatetime = calculateEndDatetime(sortedDays);
      }

      await updateDoc(eventRef, replaceUndefinedWithDeleteField(updates));
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const addEventAdminMessage = async (
    eventInfo: EventRosterInterface,
    message: Omit<ChatMessage, 'timestamp'>
  ): Promise<void> => {
    if (!eventInfo.orgId || !eventInfo.itemId) return;
    if (userState.status !== 'ready' || !isLeagueAdmin(eventInfo.leagueId, eventInfo.orgId)) {
      console.error('Permission denied: must be admin');
      return;
    }
    try {
      const eventRef = doc(firestore, `org/${eventInfo.orgId}/event/${eventInfo.itemId}`);
      await updateDoc(eventRef, {
        adminMessages: arrayUnion({ ...message, timestamp: CurrentDateTime() }),
      });
    } catch (error) {
      console.error('Error adding admin message:', error);
    }
  };

  const markAdminChatRead = async (
    orgId: string,
    leagueId: string,
    eventId: string,
    adminId: string
  ): Promise<void> => {
    if (userState.status !== 'ready' || !isLeagueAdmin(leagueId, orgId)) return;
    try {
      const eventRef = doc(firestore, `org/${orgId}/event/${eventId}`);
      await updateDoc(eventRef, { [`msgAdminGroupReadAt.${adminId}`]: CurrentDateTime() });
    } catch (error) {
      console.error('Error marking admin chat as read:', error);
    }
  };

  const markPlayerThreadsRead = async (
    orgId: string,
    leagueId: string,
    eventId: string,
    adminId: string
  ): Promise<void> => {
    if (userState.status !== 'ready' || !isLeagueAdmin(leagueId, orgId)) return;
    try {
      const eventRef = doc(firestore, `org/${orgId}/event/${eventId}`);
      await updateDoc(eventRef, { [`msgPlayerReadAt.${adminId}`]: CurrentDateTime() });
    } catch (error) {
      console.error('Error marking player threads as read:', error);
    }
  };

  return {
    addEvent,
    addEventAdminMessage,
    markAdminChatRead,
    markPlayerThreadsRead,
    deleteEvent,
    updateEvent,
  };
};

